import React from 'react';
import styled from '@emotion/styled';
import { Text, font } from '../../style-utils/text';
import {
  palette,
  radius,
  spacing,
  gradient,
  shadow,
} from '../../style-utils/theme';
import { T, m } from '../../motion';

/* No mount animation of its own any more: this bar lives in the wizard's
   persistent shell (wizard-layout.tsx) and arrives with it once. What moves
   from step to step is the fill, below. */
const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;
  max-width: 360px; /* fluid — no fixed layout width */
`;

const ProgressBarBackground = styled.div`
  height: 8px;
  width: 100%;
  background-color: ${palette.line};
  border-radius: ${radius.pill};
  position: relative;
  overflow: hidden;
`;

/* The fill is always full-width and SCALED to the progress from its left
   edge: a transform the compositor can tween, where the old width tween was a
   layout pass on every step. The eased iris gradient arrives and settles,
   never near-white. */
const ProgressBar = m.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: ${gradient.primary};
  border-radius: ${radius.pill};
  box-shadow: ${shadow.sm};
  transform-origin: left center;
`;

interface ProgressProps {
  maxPaths: number;
  currentPathIndex: number;
}

export const Progress: React.FC<ProgressProps> = ({
  maxPaths,
  currentPathIndex,
}) => {
  // maxPaths 0 = wizard entry before a type is picked — show an empty track
  // instead of a nonsensical full bar.
  const progressPercentage =
    maxPaths === 0 ? 0 : ((currentPathIndex + 2) / (maxPaths + 1)) * 100;
  const clamped = Math.max(0, Math.min(100, progressPercentage));

  return (
    <ProgressContainer>
      <Text
        id="wizard-progress-label"
        fontFamily={font.family}
        color={palette.slate}
        fontWeight={font.weight.semibold}
        mb={8}
        fontSize={font.size.xs}
        mt={0}
        pb={0}
      >
        {maxPaths === 0
          ? `Step ${currentPathIndex} `
          : `Step ${currentPathIndex + 2} of ${maxPaths + 1} Completed`}
      </Text>
      {/* The track is the progressbar: the number a screen reader gets is the
          same one the fill draws, and the visible sentence above names it. */}
      <ProgressBarBackground
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamped)}
        aria-labelledby="wizard-progress-label"
      >
        <ProgressBar
          initial={false}
          animate={{ scaleX: clamped / 100 }}
          transition={T.enter}
        />
      </ProgressBarBackground>
    </ProgressContainer>
  );
};
